import { useState, useEffect } from "react";
import { perfumeAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  PackagePlus,
  AlertTriangle,
  Package,
  DollarSign,
} from "lucide-react";

const FORMATS = ["30ml", "50ml", "100ml", "200ml", "500ml"];

const Inventory = () => {
  const [perfumes, setPerfumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isStockOpen, setIsStockOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedPerfume, setSelectedPerfume] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    quantity_ml: "",
    alert_threshold_ml: "500",
    format_costs: FORMATS.map(f => ({
      format: f,
      production_cost: "",
      packaging_cost: "",
      other_costs: "",
    })),
  });
  const [stockAmount, setStockAmount] = useState("");

  useEffect(() => {
    loadPerfumes();
  }, []);

  const loadPerfumes = async () => {
    try {
      const res = await perfumeAPI.getAll();
      setPerfumes(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei profumi");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      // Filter out formats with no costs
      const validFormatCosts = formData.format_costs
        .filter(fc => fc.production_cost || fc.packaging_cost || fc.other_costs)
        .map(fc => ({
          format: fc.format,
          production_cost: parseFloat(fc.production_cost) || 0,
          packaging_cost: parseFloat(fc.packaging_cost) || 0,
          other_costs: parseFloat(fc.other_costs) || 0,
        }));

      if (validFormatCosts.length === 0) {
        toast.error("Configura i costi per almeno un formato");
        return;
      }

      await perfumeAPI.create({
        code: formData.code,
        name: formData.name,
        quantity_ml: parseFloat(formData.quantity_ml),
        alert_threshold_ml: parseFloat(formData.alert_threshold_ml),
        format_costs: validFormatCosts,
      });
      toast.success("Profumo aggiunto con successo");
      setIsAddOpen(false);
      resetForm();
      loadPerfumes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiunta");
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const validFormatCosts = formData.format_costs
        .filter(fc => fc.production_cost || fc.packaging_cost || fc.other_costs)
        .map(fc => ({
          format: fc.format,
          production_cost: parseFloat(fc.production_cost) || 0,
          packaging_cost: parseFloat(fc.packaging_cost) || 0,
          other_costs: parseFloat(fc.other_costs) || 0,
        }));

      await perfumeAPI.update(selectedPerfume.id, {
        code: formData.code,
        name: formData.name,
        quantity_ml: parseFloat(formData.quantity_ml),
        alert_threshold_ml: parseFloat(formData.alert_threshold_ml),
        format_costs: validFormatCosts,
      });
      toast.success("Profumo aggiornato con successo");
      setIsEditOpen(false);
      resetForm();
      loadPerfumes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  const handleDelete = async () => {
    try {
      await perfumeAPI.delete(deleteId);
      toast.success("Profumo eliminato con successo");
      setDeleteId(null);
      loadPerfumes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    try {
      await perfumeAPI.addStock(selectedPerfume.id, parseFloat(stockAmount));
      toast.success(`Aggiunti ${stockAmount} litri al magazzino`);
      setIsStockOpen(false);
      setStockAmount("");
      loadPerfumes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiunta stock");
    }
  };

  const openEdit = (perfume) => {
    setSelectedPerfume(perfume);
    
    // Map existing format costs to form data
    const formatCostsMap = {};
    (perfume.format_costs || []).forEach(fc => {
      formatCostsMap[fc.format] = fc;
    });
    
    setFormData({
      code: perfume.code,
      name: perfume.name,
      quantity_ml: perfume.quantity_ml.toString(),
      alert_threshold_ml: perfume.alert_threshold_ml.toString(),
      format_costs: FORMATS.map(f => ({
        format: f,
        production_cost: formatCostsMap[f]?.production_cost?.toString() || "",
        packaging_cost: formatCostsMap[f]?.packaging_cost?.toString() || "",
        other_costs: formatCostsMap[f]?.other_costs?.toString() || "",
      })),
    });
    setIsEditOpen(true);
  };

  const openAddStock = (perfume) => {
    setSelectedPerfume(perfume);
    setIsStockOpen(true);
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      quantity_ml: "",
      alert_threshold_ml: "500",
      format_costs: FORMATS.map(f => ({
        format: f,
        production_cost: "",
        packaging_cost: "",
        other_costs: "",
      })),
    });
    setSelectedPerfume(null);
  };

  const updateFormatCost = (format, field, value) => {
    setFormData(prev => ({
      ...prev,
      format_costs: prev.format_costs.map(fc =>
        fc.format === format ? { ...fc, [field]: value } : fc
      ),
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const filteredPerfumes = perfumes.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getConfiguredFormats = (perfume) => {
    return (perfume.format_costs || []).map(fc => fc.format).join(", ") || "-";
  };

  const PerfumeForm = ({ onSubmit, isEdit = false }) => (
    <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-gray-700">Codice</Label>
          <Input
            data-testid="perfume-code-input"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            placeholder="ES001"
            required
            className="bg-gray-50 border-gray-200"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-gray-700">Nome</Label>
          <Input
            data-testid="perfume-name-input"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Essenza Royal"
            required
            className="bg-gray-50 border-gray-200"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-gray-700">Stock Iniziale (ml)</Label>
          <Input
            data-testid="perfume-quantity-input"
            type="number"
            value={formData.quantity_ml}
            onChange={(e) => setFormData({ ...formData, quantity_ml: e.target.value })}
            placeholder="1000"
            required
            className="bg-gray-50 border-gray-200"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-gray-700">Soglia Alert (ml)</Label>
          <Input
            data-testid="perfume-threshold-input"
            type="number"
            value={formData.alert_threshold_ml}
            onChange={(e) => setFormData({ ...formData, alert_threshold_ml: e.target.value })}
            placeholder="500"
            required
            className="bg-gray-50 border-gray-200"
          />
        </div>
      </div>

      {/* Format Costs */}
      <div className="space-y-2">
        <Label className="text-gray-700 flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-[#C9A227]" />
          Costi per Formato
        </Label>
        <p className="text-xs text-gray-500">
          Configura i costi per ogni formato che vendi
        </p>
        
        <Accordion type="multiple" className="space-y-2">
          {FORMATS.map((format) => {
            const fc = formData.format_costs.find(f => f.format === format);
            const hasData = fc?.production_cost || fc?.packaging_cost || fc?.other_costs;
            
            return (
              <AccordionItem
                key={format}
                value={format}
                className="border border-gray-200 rounded-lg px-4 bg-gray-50"
              >
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-900 font-medium">{format}</span>
                    {hasData && (
                      <Badge className="bg-[#C9A227]/20 text-[#C9A227] border-0 text-xs">
                        Configurato
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Produzione (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={fc?.production_cost || ""}
                        onChange={(e) => updateFormatCost(format, "production_cost", e.target.value)}
                        className="bg-gray-50 border-gray-200 h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Packaging (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={fc?.packaging_cost || ""}
                        onChange={(e) => updateFormatCost(format, "packaging_cost", e.target.value)}
                        className="bg-gray-50 border-gray-200 h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Altri (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={fc?.other_costs || ""}
                        onChange={(e) => updateFormatCost(format, "other_costs", e.target.value)}
                        className="bg-gray-50 border-gray-200 h-9"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      <Button
        type="submit"
        data-testid="perfume-submit-btn"
        className="w-full bg-[#C9A227] text-black hover:bg-[#FFD700]"
      >
        {isEdit ? "Aggiorna Profumo" : "Aggiungi Profumo"}
      </Button>
    </form>
  );

  return (
    <div data-testid="inventory-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-2">Inventario</h1>
          <p className="text-gray-500">
            Gestisci il tuo catalogo profumi con costi per formato
          </p>
        </div>
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-perfume-btn"
              className="bg-[#C9A227] text-black hover:bg-[#FFD700]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Profumo
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#C9A227]/20 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-serif text-gray-900">
                Nuovo Profumo
              </DialogTitle>
            </DialogHeader>
            <PerfumeForm onSubmit={handleAdd} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          data-testid="search-perfume-input"
          placeholder="Cerca per nome o codice..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white border-gray-200"
        />
      </div>

      {/* Table */}
      <Card className="bg-white/60 border-[#C9A227]/15 overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-[#C9A227] animate-pulse">Caricamento...</div>
            </div>
          ) : filteredPerfumes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Package className="w-12 h-12 mb-3 opacity-30" />
              <p>Nessun profumo trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-200 hover:bg-transparent">
                    <TableHead className="text-[#C9A227]">Codice</TableHead>
                    <TableHead className="text-[#C9A227]">Nome</TableHead>
                    <TableHead className="text-[#C9A227]">Formati</TableHead>
                    <TableHead className="text-[#C9A227]">Stock</TableHead>
                    <TableHead className="text-[#C9A227]">Stato</TableHead>
                    <TableHead className="text-[#C9A227] text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPerfumes.map((perfume) => (
                    <TableRow
                      key={perfume.id}
                      data-testid={`perfume-row-${perfume.code}`}
                      className="border-gray-200 hover:bg-[#C9A227]/5"
                    >
                      <TableCell className="font-medium text-gray-900">
                        {perfume.code}
                      </TableCell>
                      <TableCell className="text-gray-700">{perfume.name}</TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {getConfiguredFormats(perfume)}
                      </TableCell>
                      <TableCell>
                        <span className={perfume.is_low_stock ? "text-amber-500" : "text-gray-700"}>
                          {perfume.quantity_ml.toFixed(0)} ml
                        </span>
                      </TableCell>
                      <TableCell>
                        {perfume.is_low_stock ? (
                          <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Scorta Bassa
                          </Badge>
                        ) : (
                          <Badge className="bg-green-500/15 text-green-400 border-green-500/30">
                            OK
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              data-testid={`perfume-actions-${perfume.code}`}
                              className="h-8 w-8 p-0 text-gray-500 hover:text-[#C9A227]"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="bg-white border-gray-200"
                          >
                            <DropdownMenuItem
                              onClick={() => openAddStock(perfume)}
                              className="text-gray-700 focus:bg-[#C9A227]/10 focus:text-[#C9A227]"
                            >
                              <PackagePlus className="w-4 h-4 mr-2" />
                              Aggiungi Stock
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openEdit(perfume)}
                              className="text-gray-700 focus:bg-[#C9A227]/10 focus:text-[#C9A227]"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeleteId(perfume.id)}
                              className="text-red-400 focus:bg-red-400/10 focus:text-red-400"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-white border-[#C9A227]/20 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif text-gray-900">
              Modifica Profumo
            </DialogTitle>
          </DialogHeader>
          <PerfumeForm onSubmit={handleEdit} isEdit />
        </DialogContent>
      </Dialog>

      {/* Add Stock Dialog */}
      <Dialog open={isStockOpen} onOpenChange={setIsStockOpen}>
        <DialogContent className="bg-white border-[#C9A227]/20">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif text-gray-900">
              Aggiungi Stock
            </DialogTitle>
          </DialogHeader>
          {selectedPerfume && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-900 font-medium">{selectedPerfume.name}</p>
                <p className="text-sm text-gray-500">
                  Stock attuale: {selectedPerfume.quantity_ml.toFixed(0)} ml
                </p>
              </div>
              <form onSubmit={handleAddStock} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Quantità da Aggiungere (Litri)</Label>
                  <Input
                    data-testid="add-stock-input"
                    type="number"
                    step="0.1"
                    value={stockAmount}
                    onChange={(e) => setStockAmount(e.target.value)}
                    placeholder="1.5"
                    required
                    className="bg-gray-50 border-gray-200"
                  />
                  <p className="text-xs text-gray-500">
                    = {stockAmount ? (parseFloat(stockAmount) * 1000).toFixed(0) : 0} ml
                  </p>
                </div>
                <Button
                  type="submit"
                  data-testid="confirm-add-stock-btn"
                  className="w-full bg-[#C9A227] text-black hover:bg-[#FFD700]"
                >
                  Aggiungi al Magazzino
                </Button>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Sei sicuro di voler eliminare questo profumo? Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1A1A1A] border-gray-200 text-gray-700 hover:bg-[#27272A]">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
              className="bg-red-600 text-gray-900 hover:bg-red-700"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Inventory;
